// Client-Side Web Crypto API wrapper for AES-GCM encryption/decryption
// Generates a local, non-synced master key to ensure Zero-Knowledge edge database synchronization.

const CRYPTO_KEY_NAME = "_vedic_crypto_master_key";

// Convert string to ArrayBuffer
function stringToArrayBuffer(str: string): ArrayBuffer {
  if (typeof TextEncoder === "function") {
    try {
      const encoded = new TextEncoder().encode(str);
      const buffer = new ArrayBuffer(encoded.length);
      new Uint8Array(buffer).set(encoded);
      return buffer;
    } catch (e) {
      // fallback below
    }
  }
  const buf = new ArrayBuffer(str.length);
  const bufView = new Uint8Array(buf);
  for (let i = 0; i < str.length; i++) {
    bufView[i] = str.charCodeAt(i) & 0xff;
  }
  return buf;
}

// Convert ArrayBuffer to string
function arrayBufferToString(buf: ArrayBuffer): string {
  if (typeof TextDecoder === "function") {
    try {
      return new TextDecoder().decode(buf);
    } catch (e) {
      // fallback below
    }
  }
  const bytes = new Uint8Array(buf);
  let str = "";
  for (let i = 0; i < bytes.length; i++) {
    str += String.fromCharCode(bytes[i]);
  }
  return str;
}

// Base64 encoding helpers for ArrayBuffer
function bufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToBuffer(b64: string): ArrayBuffer {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Retrieves or generates a persistent CryptoKey for AES-GCM encryption
 */
async function getOrCreateCryptoKey(): Promise<CryptoKey> {
  if (typeof window === "undefined" || !window.crypto || !window.crypto.subtle) {
    throw new Error("Web Crypto API is not supported in this environment.");
  }

  const savedJwk = localStorage.getItem(CRYPTO_KEY_NAME);
  if (savedJwk) {
    try {
      const jwk = JSON.parse(savedJwk);
      return await window.crypto.subtle.importKey(
        "jwk",
        jwk,
        { name: "AES-GCM", length: 256 },
        true,
        ["encrypt", "decrypt"]
      );
    } catch (err) {
      console.warn("Failed to import saved cryptographic key. Generating a new one.", err);
    }
  }

  // Generate new 256-bit AES key
  const key = await window.crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"]
  );

  // Export and save to localStorage (non-synced)
  const jwk = await window.crypto.subtle.exportKey("jwk", key);
  localStorage.setItem(CRYPTO_KEY_NAME, JSON.stringify(jwk));
  return key;
}

/**
 * Encrypts a plaintext string using AES-GCM
 */
export async function encryptData(plaintext: string): Promise<string> {
  if (!plaintext) return "";
  try {
    const key = await getOrCreateCryptoKey();
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    const encryptedBuf = await window.crypto.subtle.encrypt(
      { name: "AES-GCM", iv },
      key,
      stringToArrayBuffer(plaintext)
    );

    // Combine IV and Ciphertext for storage: [IV_base64].[Ciphertext_base64]
    const ivB64 = bufferToBase64(iv.buffer);
    const cipherB64 = bufferToBase64(encryptedBuf);
    return `${ivB64}.${cipherB64}`;
  } catch (err) {
    console.error("Encryption failed:", err);
    throw err;
  }
}

/**
 * Decrypts an AES-GCM ciphertext string
 */
export async function decryptData(ciphertext: string): Promise<string> {
  if (!ciphertext) return "";
  try {
    const parts = ciphertext.split(".");
    if (parts.length !== 2) {
      // Return raw text if it is not in our encrypted format (helps with pre-existing plaintext data)
      return ciphertext;
    }

    const key = await getOrCreateCryptoKey();
    const iv = new Uint8Array(base64ToBuffer(parts[0]));
    const cipherBuf = base64ToBuffer(parts[1]);

    const decryptedBuf = await window.crypto.subtle.decrypt(
      { name: "AES-GCM", iv },
      key,
      cipherBuf
    );

    return arrayBufferToString(decryptedBuf);
  } catch (err) {
    console.warn("Decryption failed. Data might be in plaintext or corrupted.", err);
    return ciphertext; // Return raw ciphertext/plaintext as fallback
  }
}

/**
 * CryptoService provides direct AES-GCM encryption utilities for objects and arrays.
 */
export const CryptoService = {
  /**
   * Encrypts any data type (string, array, or object) to a secure string.
   */
  async encrypt(data: any): Promise<string> {
    if (data === null || data === undefined) return "";
    const plaintext = typeof data === "string" ? data : JSON.stringify(data);
    return await encryptData(plaintext);
  },

  /**
   * Decrypts a secure string back to its original object structure or string.
   */
  async decrypt<T = any>(ciphertext: string): Promise<T | null> {
    if (!ciphertext) return null;
    try {
      const plaintext = await decryptData(ciphertext);
      if (!plaintext) return null;
      try {
        return JSON.parse(plaintext) as T;
      } catch {
        return plaintext as unknown as T;
      }
    } catch (err) {
      console.warn("[CryptoService] Direct decrypt failed. Returning raw value.", err);
      return ciphertext as unknown as T;
    }
  }
};

/**
 * --- Sensitive Keys Definition ---
 */
export const SENSITIVE_LOCAL_KEYS = [
  "vedic_history",
  "calibrated_vocal_baseline",
  "vedic_pancha_yajna",
  "vedic_settings",
  "vedic_session_jwt",
  "_vedic_auth_token",
  "vedic_pro_tier_active",
];


